import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

type UserRole = 'sales' | 'factory' | 'admin' | null;

interface AdminGuardProps {
  children: React.ReactNode;
  /** 指定此路由允许的角色。不传则任意已登录用户均可访问。 */
  allowedRoles?: UserRole[];
}

export function AdminGuard({ children, allowedRoles }: AdminGuardProps) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [role, setRole] = useState<UserRole | undefined>(undefined); // undefined=加载中

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session) {
        // 获取角色
        supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.session.user.id)
          .single()
          .then(({ data: profile }) => {
            setRole((profile?.role as UserRole) ?? 'sales');
          });
      } else {
        setRole(null);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) setRole(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // 加载中
  if (session === undefined || (session !== null && role === undefined)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // 未登录
  if (!session) return <Navigate to="/admin/login" replace />;

  // 角色不符
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    // 工厂账号访问销售页 → 跳转到工厂页
    if (role === 'factory') return <Navigate to="/admin/bom" replace />;
    // 销售账号访问工厂页 → 跳转到销售看板
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

/** 导出 hook 方便子组件使用角色信息 */
export function useUserRole() {
  const [role, setRole] = useState<UserRole | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) { setRole(null); return; }
      supabase
        .from('user_profiles')
        .select('role')
        .eq('id', data.user.id)
        .single()
        .then(({ data: profile }) => {
          setRole((profile?.role as UserRole) ?? 'sales');
        });
    });
  }, []);

  return role;
}
